/**
 * CSV Import API client
 */

import axios from 'axios'
import { attachAuth } from '../../lib/apiAuth'
import type {
  CSVUploadResponse,
  CSVImportExecuteParams,
  CSVImportResponse,
  ImportTemplateInfo,
  ImportTemplateDetail,
  ImportTemplateCreate,
  EntityLevel,
  TargetTable,
  ExistingDataCheckResponse,
  ImportedDatasetInfo,
  ImportedValuesResponse,
} from '../types'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

const api = axios.create({
  baseURL: `${API_BASE}/admin/import/csv`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ============================================================================
// Upload
// ============================================================================

export async function uploadCSV(
  file: File
): Promise<CSVUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await api.post<CSVUploadResponse>(
    '/upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
  return response.data
}

// ============================================================================
// Templates
// ============================================================================

export interface GetTemplatesParams {
  provider?: string
  entityLevel?: EntityLevel
}

export async function getTemplates(
  params?: GetTemplatesParams
): Promise<ImportTemplateInfo[]> {
  const queryParams = new URLSearchParams()
  if (params?.provider) {
    queryParams.set('provider', params.provider)
  }
  if (params?.entityLevel) {
    queryParams.set('entity_level', params.entityLevel)
  }

  const queryString = queryParams.toString()
  const url = queryString ? `/templates?${queryString}` : '/templates'

  const response = await api.get<ImportTemplateInfo[]>(url)
  return response.data
}

export async function getTemplate(id: number): Promise<ImportTemplateDetail> {
  const response = await api.get<ImportTemplateDetail>(`/templates/${id}`)
  return response.data
}

export async function createTemplate(
  template: ImportTemplateCreate
): Promise<ImportTemplateInfo> {
  const response = await api.post<ImportTemplateInfo>('/templates', template)
  return response.data
}

export async function updateTemplate(
  id: number,
  update: Partial<ImportTemplateCreate>
): Promise<ImportTemplateInfo> {
  const response = await api.patch<ImportTemplateInfo>(`/templates/${id}`, update)
  return response.data
}

export async function deleteTemplate(id: number): Promise<void> {
  await api.delete(`/templates/${id}`)
}

// ============================================================================
// Check Existing Data
// ============================================================================

export async function checkExistingData(
  uploadId: string,
  targetTable: TargetTable,
  referenceYear: number
): Promise<ExistingDataCheckResponse> {
  const response = await api.get<ExistingDataCheckResponse>(
    `/check-existing/${uploadId}`,
    {
      params: {
        target_table: targetTable,
        reference_year: referenceYear,
      },
    }
  )
  return response.data
}

// ============================================================================
// Execute
// ============================================================================

export async function executeCSVImport(
  uploadId: string,
  params: CSVImportExecuteParams,
  background: boolean = true,
  force: boolean = false
): Promise<CSVImportResponse> {
  const response = await api.post<CSVImportResponse>(
    `/execute/${uploadId}?background=${background}&force=${force}`,
    params
  )
  return response.data
}

// ============================================================================
// Imported Data
// ============================================================================

export async function getImportedData(): Promise<ImportedDatasetInfo[]> {
  const response = await api.get<ImportedDatasetInfo[]>('/data')
  return response.data
}

export async function getImportedValues(
  dataSourceId: number,
  indicatorId: number,
  referenceYear?: number,
  limit: number = 500,
  offset: number = 0
): Promise<ImportedValuesResponse> {
  const response = await api.get<ImportedValuesResponse>(
    `/data/${dataSourceId}/values`,
    {
      params: {
        indicator_id: indicatorId,
        ...(referenceYear !== undefined ? { reference_year: referenceYear } : {}),
        limit,
        offset,
      },
    }
  )
  return response.data
}

// ============================================================================
// Status
// ============================================================================

export interface CSVImportStatus {
  job_id: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: {
    current: number
    total: number
    percentage: number
    message: string
  }
  started_at: string | null
  completed_at: string | null
  errors: string[]
  // Result fields (when completed)
  total_records?: number
  successful_records?: number
  failed_records?: number
  skipped_records?: number
  success_rate?: number
  duration_seconds?: number
  warnings?: string[]
  target_table?: string
  reference_year?: number
}

export async function getCSVImportStatus(jobId: string): Promise<CSVImportStatus> {
  const response = await api.get<CSVImportStatus>(`/status/${jobId}`)
  return response.data
}

// Basic-Auth-Header + 401-Behandlung (optionaler Schutz, v0.3.8)
attachAuth(api)
